import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Box, Skeleton, styled, Switch, Typography } from '@mui/material';

import fresh_start_avatar_icon from 'Assets/images/fresh_start_avatar_icon.svg';
import { LoginState, useSelectLoginState } from 'Auth/data';
import { CloseIconButton } from 'Common/CloseIconButton';
import { ConfirmationDialog } from 'Common/ConfirmationDialog/ConfirmationDialog';
import { AvatarConsts } from 'Common/UserAvatar/Avatar/Avatar.consts';
import { Card as BaseCard } from 'Components';
import type { CourseModel } from 'Courses/data';
import {
  saveIsFreshStartMachineReadyToClone,
  saveWhetherTrainerAllowedPreview,
  stopFreshStartDesktop,
  useSelectIsFreshStartDesktopLoading,
  useSelectIsMyDesktopLoading
} from 'Courses/data';
import { urls } from 'Router';
import { useAppDispatch } from 'Store';
import { Colors } from 'Themes/colors';
import { Spacing } from 'Themes/spacing';
import { Typo } from 'Themes/typo';
import { saveAreTipsHidden, useSelectCurrentUserInfo, useSelectUserDisplayName } from 'User/data';
import type { UserDisplayModel } from 'User/userDisplayModel';
import { useBoolean } from 'Utils/customHooks';

import type {
  ISaveIsFreshStartMachineReadyToCloneRequest,
  ISaveWhetherTrainerAllowedPreviewRequest
} from '@libs/models';

import { BasicFullscreenProvider } from '../../../components/fullscreen-desktop-viewer/basic-fullscreen-context';
import { BasicFullscreenViewer } from '../../../components/fullscreen-desktop-viewer/basic-fullscreen-viewer';
import { LivePreviewCard } from '../../../components/shared';

import { Divider } from '../common/divider';

import { ChatCloud } from './chat-cloud';
import { Chip } from './chip';

import type { DesktopModel } from 'Api';

const Container = styled(Box)`
  display: flex;
  gap: ${Spacing.large};
`;

const Section = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 25px;
`;

const SwitchContainer = styled(Box)`
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 4px;
  gap: 15px;
  border: 1px solid ${Colors.darkGrey};
`;

const TitleContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InfoChip = styled(Chip)`
  cursor: pointer;
`;

const LivePreviewContainer = styled(Box)<{ areTipsHidden?: boolean }>`
  ${({ areTipsHidden }) =>
    !areTipsHidden &&
    `
      margin-top: -6rem;
      margin-bottom: ${Spacing.large};
    `}
`;

const StyledCloseIconButton = styled(CloseIconButton)`
  position: absolute !important;
  top: ${Spacing.small};
  right: ${Spacing.small};
`;

type Props = {
  course: CourseModel;
};

const SettingsContent = ({ course }: Readonly<Props>) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const Card = styled(BaseCard)`
    gap: 46px;
    display: flex;
    flex-direction: column;
    position: relative;
  `;

  const [stopFreshStartDialogIsDisplayed, displayStopFreshStartDialog, hideStopFreshStartDialog] =
    useBoolean();

  const isFreshStartDesktopLoading = useSelectIsFreshStartDesktopLoading(course.id as string);
  const isMyDesktopLoading = useSelectIsMyDesktopLoading(course.id as string);

  const currentUserInfo = useSelectCurrentUserInfo();
  const currentUserInfoDisplayName = useSelectUserDisplayName() ?? AvatarConsts.fallbackUserName;

  const currentUserModel = {
    displayName: currentUserInfoDisplayName,
    avatarColor: currentUserInfo.avatarColor,
    avatar: currentUserInfo.avatar
  } as UserDisplayModel;

  const loginState = useSelectLoginState();
  const isLogged = loginState === LoginState.LOGGED;

  const handleSaveWhetherTrainerAllowedPreviewChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(
      saveWhetherTrainerAllowedPreview({
        courseId: course.id,
        whetherTrainerAllowedPreview: event.target.checked
      } as ISaveWhetherTrainerAllowedPreviewRequest)
    );
  };

  const showTips = () => handleSaveAreTipsHiddenChange(false);
  const hideTips = () => handleSaveAreTipsHiddenChange(true);

  const handleSaveAreTipsHiddenChange = (value: boolean) => {
    dispatch(
      saveAreTipsHidden({
        areTipsHidden: value,
        isLogged
      })
    );
  };

  const handleSaveIsFreshStartMachineReadyToCloneChange = (value: boolean) => {
    dispatch(
      saveIsFreshStartMachineReadyToClone({
        courseId: course.id,
        isFreshStartMachineReadyToClone: value
      } as ISaveIsFreshStartMachineReadyToCloneRequest)
    );

    hideStopFreshStartDialog();
  };

  const handleStopFreshStartMachine = () => {
    dispatch(
      stopFreshStartDesktop({
        courseId: course.id as string,
        desktopInstanceId: course?.freshStartDesktop?.instanceId as number
      })
    );
  };

  const onSaveIsFreshStartMachineReadyToCloneChange = () => {
    if (course?.isFreshStartMachineReadyToClone) {
      handleSaveIsFreshStartMachineReadyToCloneChange(false);
    } else if (course?.freshStartDesktop?.isRunning) {
      displayStopFreshStartDialog();
    } else {
      handleSaveIsFreshStartMachineReadyToCloneChange(true);
    }
  };

  const freshStartMachineIsReadyToCloneSwitch = (
    <SwitchContainer>
      <Switch
        size="small"
        checked={course.isFreshStartMachineReadyToClone}
        onChange={() => onSaveIsFreshStartMachineReadyToCloneChange()}
        slotProps={{
          input: { 'aria-label': 'controlled' }
        }}
      />
      <Typography>{t('FreshStartMachineIsReadyToClone')}</Typography>
    </SwitchContainer>
  );

  const sharePreviewOfMyMachineSwitch = (
    <SwitchContainer>
      <Switch
        size="small"
        checked={course.whetherTrainerAllowedPreview}
        onChange={handleSaveWhetherTrainerAllowedPreviewChange}
        slotProps={{
          input: { 'aria-label': 'controlled' }
        }}
      />
      <Typography>{t('SharePreviewOfMyMachine')}</Typography>
    </SwitchContainer>
  );

  const navigate = useNavigate();

  return (
    <>
      <Card>
        <TitleContainer>
          <Typography variant={Typo.title}>{t('courseSettings.title')}</Typography>
          {currentUserInfo?.areTipsHidden && (
            <InfoChip color={Colors.blue} onClick={showTips}>
              i
            </InfoChip>
          )}
        </TitleContainer>

        <Container>
          <Section>
            {!currentUserInfo?.areTipsHidden && (
              <ChatCloud
                number={1}
                text={t('courseSettings.descriptionChatCloud1')}
                onClose={hideTips}
              />
            )}

            {isFreshStartDesktopLoading ? (
              <Skeleton variant="rounded" animation="wave" width={220} height={33} key={0} />
            ) : (
              <LivePreviewContainer areTipsHidden={currentUserInfo?.areTipsHidden}>
                <LivePreviewCard
                  staticSize={!currentUserInfo?.areTipsHidden}
                  course={course}
                  user={
                    {
                      displayName: t('FreshStart'),
                      customAvatar: (
                        <img src={fresh_start_avatar_icon} alt="fresh start avatar icon" />
                      )
                    } as UserDisplayModel
                  }
                  desktop={course.freshStartDesktop as DesktopModel}
                  isFreshStartDesktop
                />
              </LivePreviewContainer>
            )}

            {!currentUserInfo?.areTipsHidden ? (
              <ChatCloud
                number={2}
                text={t('courseSettings.descriptionChatCloud2')}
                onClose={hideTips}
              >
                {freshStartMachineIsReadyToCloneSwitch}
              </ChatCloud>
            ) : (
              freshStartMachineIsReadyToCloneSwitch
            )}
          </Section>

          <Divider />

          <Section>
            {!currentUserInfo?.areTipsHidden && (
              <ChatCloud
                number={3}
                text={t('courseSettings.descriptionChatCloud3')}
                onClose={hideTips}
              />
            )}

            {isMyDesktopLoading ? (
              <Skeleton variant="rounded" animation="wave" width={220} height={33} key={0} />
            ) : (
              <LivePreviewContainer areTipsHidden={currentUserInfo?.areTipsHidden}>
                <LivePreviewCard
                  staticSize={!currentUserInfo?.areTipsHidden}
                  course={course}
                  user={currentUserModel}
                  desktop={course.myDesktop as DesktopModel}
                  isTrainerDesktop
                  isHighlighted
                />
              </LivePreviewContainer>
            )}

            {!currentUserInfo?.areTipsHidden ? (
              <ChatCloud
                number={4}
                text={t('courseSettings.descriptionChatCloud4')}
                onClose={hideTips}
              >
                {sharePreviewOfMyMachineSwitch}
              </ChatCloud>
            ) : (
              sharePreviewOfMyMachineSwitch
            )}
          </Section>
        </Container>
        <StyledCloseIconButton onClose={() => navigate(urls.coursePage(course.id as string))} />
      </Card>

      <ConfirmationDialog
        open={stopFreshStartDialogIsDisplayed}
        content={`${t('StopFreshStartWarning')} ${t('AreYouSure')}`}
        onNo={hideStopFreshStartDialog}
        onYes={() => {
          handleSaveIsFreshStartMachineReadyToCloneChange(true);
          handleStopFreshStartMachine();
        }}
      />
    </>
  );
};

export const Settings = ({ course }: Readonly<Props>) => {
  return (
    <BasicFullscreenProvider>
      <SettingsContent course={course} />
      <BasicFullscreenViewer />
    </BasicFullscreenProvider>
  );
};
